import { Component, OnInit, Input, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SeriesLineOptions } from "highcharts";

import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { AssemblyConfig, APIResults } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-trend-ci",
	templateUrl: "./trend-ci.component.html",
	styleUrls: ["./trend-ci.component.scss"],
	imports: [DvsChartComponent, FormsModule]
})
export class TrendCiComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	component: AssemblyConfig.Component;

	public brand = BrandVars;

	public regimes: APIResults.RegimeIndex;
	public maintenancehistory: APIResults.MaintenanceHistory[] = undefined;

	public showOptional = false;

	public useXAxisDate = BrandVars.fixedassets;
	public compareRegimes = false;
	public dataExceedsLimit = false;
	public unlimitedChart = false;

	public ciCriticals: CIWithOptions[] = [];
	public ciOptionals: CIWithOptions[] = [];

	async ngOnInit() {
		this.showOptional = false;
		await this.RefreshChart();
	}

	public async RefreshChart() {
		if (this.asset && this.component) {
			// Fetch the regimes to use later in the regime CI chart, if needed
			const [regimes, maintresult] = await Promise.all([
				this.api.GetRegimes(this.asset.asset_id),
				this.api.GetMaintenanceHistory(this.asset.asset_id),
			]);
			this.regimes = regimes;
			this.maintenancehistory = maintresult;
			this.ciCriticals = this.component.ci.filter(item => item.plot === undefined);
			await Promise.all(this.ciCriticals.map(async ci => {
				await this.PopulateCiOptions(ci);
			}));
			if (this.showOptional) {
				await this.enableOptionals();
			}
		}
	}

	async enableOptionals() {
		if (this.asset && this.component) {
			this.showOptional = true;
			this.ciOptionals = this.component.optional_ci.filter(item => item.plot === undefined);
			await Promise.all(this.ciOptionals.map(async ci => {
				await this.PopulateCiOptions(ci);
			}));
		}
	}
	async PopulateCiOptions(ci: CIWithOptions) {
		const xaxisTitle = this.useXAxisDate ? "Date" : (BrandVars.flightTime + " (hrs)");
		ci.options = undefined;
		ci.errormessage = undefined;
		const siblings = [ci];
		// If we have a paired plot CI, add it
		const dependent = this.component.ci.find(item => item.plot === ci.index) || this.component.optional_ci.find(item => item.plot === ci.index);
		if (dependent) {
			siblings.push(dependent);
		}
		try {
			const results = await Promise.all(siblings.map(async sibling => {
				const ciresult = await this.api.GetComponentCi(this.asset.asset_id, this.component.id, sibling.index, 0, this.unlimitedChart ? 1000000 : 15000);
				if (this.useXAxisDate) {
					for (let i = 0; i < ciresult.ci.length; i++) {
						ciresult.ci[i][0] = ciresult.dates[i] * 1000;
						ciresult.regimes[i] = [ciresult.dates[i] * 1000, <any>ciresult.regimes[i]];
					}
				} else {
					// Copy x-axis to the regimes
					for (let i = 0; i < ciresult.ci.length; i++) {
						ciresult.regimes[i] = [ciresult.ci[i][0], <any>ciresult.regimes[i]];
					}
				}
				this.dataExceedsLimit = (ciresult.ci && ciresult.ci.length >= 15000);
				return {
					ci: sibling,
					dates: ciresult.dates,
					data: ciresult.ci,
					regimes: ciresult.regimes,
				};
			}));
			const series: SeriesLineOptions[] = results.map(formattedci => {
				return {
					type: "line",
					name: formattedci.ci.name,
					data: formattedci.data,
				};
			});
			series.push({
				type: "line",
				name: "Regime",
				// Don't fade out when hovering on the main data
				states: { inactive: { enabled: false } },
				visible: false,
				showInLegend: false,
				enableMouseTracking: false,
				zIndex: -1,
				color: "rgba(100, 100, 100, 0.5)",
				// Note - could be a little wonky if there are siblings and one is somehow missing data
				data: results[0].regimes,
			});
			const dates = results.map(result => result.dates);
			const pointDetails = (point: Highcharts.Point) => {
				const regime_id = (<any>ci.options.series[ci.options.series.length - 1]).data[point.index][1];
				if (this.regimes && this.regimes[regime_id]) {
					return "<br/>Regime: " + this.regimes[regime_id].name;
				}
				return "";
			};
			ci.options = this.charting.FlightTimeWithMaint(ci.groupname || ci.name, ci.detail, xaxisTitle, this.useXAxisDate, series, dates, pointDetails, ci.units, this.fleet ? this.fleet.time_offset_hours : 0, ci.warning, ci.alarm, this.maintenancehistory, this.component.lru, this.compareRegimes);
		} catch (err) {
			console.log(err);
			ci.errormessage = "Couldn't load CI trend";
		}
	}
}

interface CIWithOptions extends AssemblyConfig.CI {
	options?: Highcharts.Options;
	errormessage?: string;
}
